import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const COMPANY = "RIMITANA GLOBAL NETWORKS PRIVATE LIMITED";
const EMAIL = "rimitananetwork@gmail.com";
const PHONE = "+91 9690 181 181";
const SITE = "www.animalmela.com";

const SECTIONS = [
  {
    h: "Introduction",
    p: [
      `AnimalMela ("we", "our", "us") is a digital platform for Pashu Haats (livestock markets) operated by ${COMPANY}. This Privacy Policy explains how we collect, use, store and protect your information when you use the AnimalMela website, Progressive Web App and Android application (together, the "Service").`,
      "By using AnimalMela you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the Service.",
    ],
  },
  {
    h: "Information We Collect",
    p: ["We collect only the information needed to run digital haat operations:"],
    list: [
      "Account details — name, mobile number, role (owner / helper) and optional email address.",
      "Haat operation records — seller and purchaser details (name, mobile, optional Aadhaar number, address), animal details (type, breed, colour, age, weight, ear tag, photos), payment receipts, gate passes and vehicle numbers entered by authorised users.",
      "Device and usage data — device identifier, browser type, IP address, login timestamps and sync status, used for security and offline synchronisation.",
      "Approximate location — derived from your IP address to verify service availability in your region.",
    ],
  },
  {
    h: "How We Use Information",
    list: [
      "To register animals, issue QR gate passes and generate payment receipts.",
      "To provide dashboards, reports and daily summaries to the Pashu Haat owner.",
      "To secure accounts, prevent fraud and enforce role-based access.",
      "To send service notifications (receipts, gate passes, daily digests) via WhatsApp, SMS or email where enabled.",
      "To improve the reliability and performance of the Service.",
    ],
  },
  {
    h: "Offline Data",
    p: [
      "AnimalMela is offline-first. When your device has no internet connection, records you create are stored on your device in an encrypted local database (AES-256 encryption) and automatically synchronised to our servers when you are back online.",
      "Offline data remains protected on the device and is removed when you sign out or when a device is remotely revoked by the account owner.",
    ],
  },
  {
    h: "Data Security",
    p: [
      "We use industry-standard safeguards: encrypted connections (HTTPS), encrypted on-device storage, hashed passwords, token-based sessions, device binding with remote revocation, and tenant isolation so each haat's data is accessible only to its own authorised users.",
      "No method of transmission or storage is 100% secure; we continuously work to protect your information but cannot guarantee absolute security.",
    ],
  },
  {
    h: "Data Sharing",
    p: [
      "We do not sell or rent your personal information. Data is shared only:",
    ],
    list: [
      "Within your haat — with the haat owner and helpers authorised for that haat.",
      "With service providers who help us run the platform (e.g. cloud hosting, email delivery), bound by confidentiality obligations.",
      "When required by law, regulation or a valid request from public authorities.",
    ],
  },
  {
    h: "WhatsApp & SMS",
    p: [
      "With your consent, receipts, gate passes and summaries may be shared via WhatsApp or SMS to the mobile numbers recorded during registration. Message delivery is handled by the respective platform (e.g. WhatsApp/Meta, telecom operators) and is subject to their own privacy policies.",
      "One-time passwords (OTPs) may be sent by SMS to verify your mobile number at login.",
    ],
  },
  {
    h: "Camera Permission",
    p: [
      "The app requests camera access only to scan QR gate passes and ear tags, and to photograph animals during registration. Images are captured only when you tap the camera or upload button. We do not access your camera in the background.",
    ],
  },
  {
    h: "Location Permission",
    p: [
      "We use approximate, IP-based location to confirm the Service is being used within supported regions (currently India). We do not track your precise GPS location or your movement.",
    ],
  },
  {
    h: "Storage Permission",
    p: [
      "Storage access is used to save downloaded receipts and gate pass PDFs to your device, and to let you upload existing photos of animals or QR codes. We only read files you explicitly select.",
    ],
  },
  {
    h: "Data Retention",
    p: [
      "Haat operation records are retained for as long as the haat owner's subscription remains active, and thereafter for the period required for legal, accounting or dispute-resolution purposes. You may request deletion of your personal data as described under User Rights.",
    ],
  },
  {
    h: "Children's Privacy",
    p: [
      "AnimalMela is a business tool intended for adults. We do not knowingly collect personal information from children under 18. If you believe a child has provided us information, contact us and we will delete it.",
    ],
  },
  {
    h: "User Rights",
    p: ["You may, at any time:"],
    list: [
      "Request access to the personal information we hold about you.",
      "Request correction of inaccurate information.",
      "Request deletion of your personal data, subject to legal retention requirements.",
      "Withdraw consent for optional communications such as WhatsApp notifications.",
    ],
    after: `To exercise these rights, contact us at ${EMAIL} or ${PHONE}.`,
  },
  {
    h: "Third Party Services",
    p: [
      "The Service relies on trusted third parties such as cloud hosting providers, email delivery services and messaging platforms (WhatsApp, SMS). These providers process data only to deliver their service to us and are governed by their own privacy policies.",
    ],
  },
  {
    h: "Changes to this Privacy Policy",
    p: [
      "We may update this Privacy Policy from time to time. The updated version will be posted on this page with a revised effective date. Continued use of the Service after changes take effect constitutes acceptance of the updated policy.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "AnimalMela Privacy Policy";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const prevDesc = meta.content;
    meta.content = "Read the official Privacy Policy of AnimalMela operated by RIMITANA GLOBAL NETWORKS PRIVATE LIMITED.";
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else meta.content = prevDesc;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="privacy-policy-page">
      {/* Header (hidden on print) */}
      <header className="border-b print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-home-link">
            <BrandLogo size="footer" alt="AnimalMela" />
            <span className="font-display font-extrabold">AnimalMela</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900" data-testid="privacy-print-btn">
              <Printer className="w-4 h-4" /> Print
            </button>
            <Link to="/login" className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-900" data-testid="privacy-back-login">
              <ArrowLeft className="w-4 h-4" /> Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-base font-semibold">AnimalMela</p>
        <p className="text-sm text-gray-500">A Product of {COMPANY}</p>
        <p className="mt-2 text-sm text-gray-500">Effective Date: <span className="font-semibold text-gray-700">11 August 2026</span></p>

        <div className="mt-10 space-y-10">
          {SECTIONS.map((s, i) => (
            <section key={s.h} data-testid={`privacy-section-${i}`}>
              <h2 className="text-lg font-bold mb-3">{i + 1}. {s.h}</h2>
              {(s.p || []).map((para, j) => (
                <p key={j} className="text-[15px] leading-7 text-gray-700 mb-3">{para}</p>
              ))}
              {s.list && (
                <ul className="list-disc pl-6 space-y-2 text-[15px] leading-7 text-gray-700">
                  {s.list.map((li, j) => <li key={j}>{li}</li>)}
                </ul>
              )}
              {s.after && <p className="text-[15px] leading-7 text-gray-700 mt-3">{s.after}</p>}
            </section>
          ))}

          <section data-testid="privacy-section-contact">
            <h2 className="text-lg font-bold mb-3">{SECTIONS.length + 1}. Contact Us</h2>
            <p className="text-[15px] leading-7 text-gray-700 mb-4">
              For questions about this Privacy Policy or your personal data, reach us at:
            </p>
            <div className="rounded-2xl border p-5 text-[15px] leading-8">
              <div><span className="font-semibold">Company:</span> {COMPANY}</div>
              <div><span className="font-semibold">Website:</span> <a href={`https://${SITE}`} className="text-green-700 underline underline-offset-2" target="_blank" rel="noreferrer">{SITE}</a></div>
              <div><span className="font-semibold">Email:</span> <a href={`mailto:${EMAIL}`} className="text-green-700 underline underline-offset-2">{EMAIL}</a></div>
              <div><span className="font-semibold">Phone:</span> <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="text-green-700 underline underline-offset-2">{PHONE}</a></div>
            </div>
          </section>
        </div>

        <footer className="mt-14 pt-6 border-t text-xs text-gray-400">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </footer>
      </main>
    </div>
  );
}
